import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { externalVendorsApi } from '../api/externalVendors';
import { invoicesApi } from '../api/invoices';
import { vendorsApi } from '../api/vendors';

/**
 * The matching dashboard reads from three separate `createApi` slices: matches and runs from
 * `invoicesApi`, the invoice/transaction/metrics tables from `externalVendorsApi`, and
 * underwriting transaction detail from `vendorsApi`. RTK Query tag invalidation never crosses a
 * slice boundary, so a mutation in `invoicesApi` leaves the other two showing stale rows until a
 * page reload — which is what operators kept hitting.
 *
 * This listener bridges that gap: any matching mutation that lands invalidates the matching
 * views in the other two slices as well. It lives outside the slices on purpose, since importing
 * one slice from another would make the graph circular.
 */
export const matchingCacheInvalidation = createListenerMiddleware();

const matchingMutations = [
  invoicesApi.endpoints.triggerTransactionMatchingRun,
  invoicesApi.endpoints.confirmTransactionMatch,
  invoicesApi.endpoints.rejectTransactionMatch,
  invoicesApi.endpoints.confirmTransactionMatchGroup,
  invoicesApi.endpoints.rejectTransactionMatchGroup,
  invoicesApi.endpoints.createTransactionMatch,
] as const;

matchingCacheInvalidation.startListening({
  matcher: isAnyOf(...matchingMutations.map((endpoint) => endpoint.matchFulfilled)),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(externalVendorsApi.util.invalidateTags(['MatchingDashboard']));
    listenerApi.dispatch(vendorsApi.util.invalidateTags(['Vendor Account Underwriting Transactions']));
  },
});

export default matchingCacheInvalidation;
